import { ref } from 'vue'

export const useResize = () => {
        const resizingImage = ref(null)
        const resizeDirection = ref(null)
        const startResizePosition = ref({ x: 0, y: 0 })
        const startImageSize = ref({ width: 0, height: 0 })
        const startImagePosition = ref({ x: 0, y: 0 })
        const startAspectRatio = ref(null)
        const minWidth = ref(50)
        const minHeight = ref(50)
        const maxWidth = ref(Infinity)
        const maxHeight = ref(Infinity)

        const getEventCoordinates = event => {
                if (event.type.includes('touch')) {
                        return {
                                clientX: event.touches[0].clientX,
                                clientY: event.touches[0].clientY,
                        }
                }
                return {
                        clientX: event.clientX,
                        clientY: event.clientY,
                }
        }

        const startResize = (
                droppedImages,
                image,
                event,
                direction,
                options = {},
                onResizeEnd = null
        ) => {
                event.preventDefault()
                event.stopPropagation()

                resizingImage.value = image
                resizeDirection.value = direction
                
                const scale = options.scale || 1

                const coords = getEventCoordinates(event)
                startResizePosition.value = { x: coords.clientX, y: coords.clientY }
                startImageSize.value = { width: image.width, height: image.height }
                startImagePosition.value = { x: image.x, y: image.y }
                startAspectRatio.value = options.aspectRatio || image.width / image.height
                minWidth.value = options.minWidth || 50
                minHeight.value = options.minHeight || 50
                maxWidth.value = options.maxWidth || Infinity
                maxHeight.value = options.maxHeight || Infinity

                const handleResize = e => {
                        if (!resizingImage.value || !resizeDirection.value) return

                        const coords = getEventCoordinates(e)
                        const deltaX = (coords.clientX - startResizePosition.value.x) / scale
                        const deltaY = (coords.clientY - startResizePosition.value.y) / scale
                        const newImage = { ...resizingImage.value }
                        const flipped = newImage.flip === true
                        const direction = resizeDirection.value
                        const aspectRatio = startAspectRatio.value

                        const clamp = (val, min, max) => Math.min(Math.max(val, min), max)

                        const setWidthHeight = (width, height) => {
                                newImage.width = clamp(width, minWidth.value, maxWidth.value)
                                newImage.height = clamp(height, minHeight.value, maxHeight.value)
                        }

                        if (
                                ['top-left', 'top-right', 'bottom-left', 'bottom-right'].includes(
                                        direction
                                )
                        ) {
                                let scaleX = 1,
                                        scaleY = 1

                                switch (direction) {
                                        case 'top-right':
                                                scaleY = 1 - deltaY / startImageSize.value.height
                                                scaleX = scaleY
                                                break
                                        case 'top-left':
                                                scaleX = 1 - deltaX / startImageSize.value.width
                                                scaleY = scaleX
                                                break
                                        case 'bottom-left':
                                                scaleX = 1 - deltaX / startImageSize.value.width
                                                scaleY = scaleX
                                                break
                                        case 'bottom-right':
                                                scaleX = 1 + deltaX / startImageSize.value.width
                                                scaleY = scaleX
                                                break
                                }

                                const newWidth = startImageSize.value.width * scaleX
                                const newHeight = newWidth / aspectRatio

                                setWidthHeight(newWidth, newHeight)

                                // Корректируем позицию для углов, где x или y меняются
                                if (direction.includes('left')) {
                                        newImage.x =
                                                startImagePosition.value.x +
                                                (startImageSize.value.width - newImage.width)
                                }
                                if (direction.includes('top')) {
                                        newImage.y =
                                                startImagePosition.value.y +
                                                (startImageSize.value.height - newImage.height)
                                }
                        } else {
                                // Для боковых и верхних/нижних ручек с учётом flip
                                // Mark as distorted when using edge handles (allows non-proportional resize)
                                newImage.distorted = true
                                switch (direction) {
                                        case 'right':
                                                if (!flipped) {
                                                        const newWidth = clamp(
                                                                startImageSize.value.width + deltaX,
                                                                minWidth.value,
                                                                maxWidth.value
                                                        )
                                                        newImage.width = newWidth
                                                } else {
                                                        // При flip меняем высоту
                                                        const newHeight = clamp(
                                                                startImageSize.value.height - deltaX,
                                                                minHeight.value,
                                                                maxHeight.value
                                                        )
                                                        newImage.height = newHeight
                                                        newImage.y =
                                                                startImagePosition.value.y +
                                                                (startImageSize.value.height - newHeight)
                                                }
                                                break

                                        case 'left':
                                                if (!flipped) {
                                                        const newWidth = clamp(
                                                                startImageSize.value.width - deltaX,
                                                                minWidth.value,
                                                                maxWidth.value
                                                        )
                                                        newImage.width = newWidth
                                                        newImage.x =
                                                                startImagePosition.value.x +
                                                                (startImageSize.value.width - newWidth)
                                                } else {
                                                        const newHeight = clamp(
                                                                startImageSize.value.height + deltaX,
                                                                minHeight.value,
                                                                maxHeight.value
                                                        )
                                                        newImage.height = newHeight
                                                }
                                                break

                                        case 'bottom':
                                                {
                                                        const newHeight = clamp(
                                                                startImageSize.value.height + deltaY,
                                                                minHeight.value,
                                                                maxHeight.value
                                                        )
                                                        newImage.height = newHeight
                                                }
                                                break

                                        case 'top':
                                                {
                                                        const newHeight = clamp(
                                                                startImageSize.value.height - deltaY,
                                                                minHeight.value,
                                                                maxHeight.value
                                                        )
                                                        if (newHeight >= minHeight.value) {
                                                                newImage.height = newHeight
                                                                newImage.y =
                                                                        startImagePosition.value.y +
                                                                        (startImageSize.value.height - newHeight)
                                                        }
                                                }
                                                break
                                }
                        }

                        const index = droppedImages.findIndex(img => img.id === newImage.id)
                        if (index !== -1) {
                                droppedImages[index] = newImage
                        }
                }

                const endResize = () => {
                        document.removeEventListener('mousemove', handleResize)
                        document.removeEventListener('touchmove', handleResize)
                        document.removeEventListener('mouseup', endResize)
                        document.removeEventListener('touchend', endResize)
                        
                        // Call callback when resize ends
                        if (onResizeEnd && typeof onResizeEnd === 'function') {
                                onResizeEnd()
                        }
                        
                        resizingImage.value = null
                        resizeDirection.value = null
                }

                document.addEventListener('mousemove', handleResize)
                document.addEventListener('touchmove', handleResize, { passive: false })
                document.addEventListener('mouseup', endResize)
                document.addEventListener('touchend', endResize)
        }

        return { startResize }
}
